function resolveNumber(...values) {
  for (const value of values) {
    if (
      value !== null &&
      value !== undefined &&
      value !== "" &&
      Number.isFinite(Number(value))
    ) {
      return Number(value);
    }
  }

  return null;
}


function getCoverageCount(
  coverages,
  predicate
) {
  if (!Array.isArray(coverages)) {
    return null;
  }

  return coverages.filter(predicate).length;
}


function normalizeStatus(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}


function isActiveCoverage(coverage) {
  const status = normalizeStatus(
    coverage?.status
  );

  return (
    status === "active" ||
    status === "in_force" ||
    status === "in force"
  );
}


function isExpiringCoverage(coverage) {
  const status = normalizeStatus(
    coverage?.status
  );

  return (
    status === "expiring" ||
    status === "expiring_soon" ||
    status === "expiring soon"
  );
}


export function adaptRiskFinancingEngine(
  engine
) {
  if (
    !engine ||
    engine.engineId !== "risk_financing"
  ) {
    return {
      available: false,
      status: "idle",
      overallPosition: {},
      recommendations: [],
      sourceData: null,
    };
  }

  /*
   * The Engine normally exposes the normalized
   * summary directly under portfolio.summary.
   *
   * portfolio.data.summary is retained only as
   * a defensive fallback for API-level data.
   */
  const portfolioSummary =
    engine.portfolio?.summary ||
    engine.portfolio?.data?.summary ||
    engine.portfolio?.data ||
    {};

  const portfolioAppetite =
    engine.portfolio?.data?.appetite ||
    portfolioSummary.appetite ||
    {};

  const coverageAdequacy =
    engine.coverage?.adequacy || {};

  const adequacySummary =
    engine.coverage?.summary ||
    coverageAdequacy?.summary ||
    {};

  const coverages =
    Array.isArray(
      engine.coverage?.portfolio
    )
      ? engine.coverage.portfolio
      : [];

  /*
   * Normalize the Risk Financing Engine contract
   * for consumption by the Decision Center.
   *
   * Current Risk Financing API fields:
   *
   * total_eal
   * residual_eal
   * transferred_pct
   *
   * Additional aliases are retained as defensive
   * fallbacks for older or alternative responses.
   */
  const expectedAnnualLoss =
    resolveNumber(
      portfolioSummary.total_eal,
      portfolioSummary.expected_annual_loss,
      portfolioSummary.eal,
      portfolioSummary.expectedAnnualLoss
    );

  const residualRisk =
    resolveNumber(
      portfolioSummary.residual_eal,
      portfolioSummary.residual_risk,
      portfolioSummary.expected_residual_loss,
      portfolioSummary.company_residual_risk
    );

  const coverageRatio =
    resolveNumber(
      portfolioSummary.transferred_pct,
      portfolioSummary.transfer_percentage,
      portfolioSummary.coverage_ratio,
      adequacySummary.coverage_ratio,
      coverageAdequacy.coverage_ratio
    );

  const capitalAtRiskP95 =
    resolveNumber(
      portfolioSummary.p95_loss,
      portfolioSummary.p95,
      portfolioSummary.loss_p95,
      portfolioSummary.capital_at_risk_p95,
      portfolioSummary.capitalAtRiskP95,

      adequacySummary.capital_at_risk_p95,
      adequacySummary.capitalAtRiskP95,

      coverageAdequacy.capital_at_risk_p95,
      coverageAdequacy.capitalAtRiskP95
    );

  const scenariosWithProtectionGaps =
    resolveNumber(
      adequacySummary
        .scenarios_with_protection_gaps_count,

      coverageAdequacy
        .scenarios_with_protection_gaps_count
    );

  const activeScenarios =
    resolveNumber(
      adequacySummary.active_scenarios,
      coverageAdequacy.active_scenarios,
      portfolioSummary.active_scenarios
    );

  const coveredScenarios =
    resolveNumber(
      adequacySummary.covered_scenarios,
      coverageAdequacy.covered_scenarios,
      portfolioSummary.covered_scenarios
    );

  const uncoveredScenarios =
    resolveNumber(
      adequacySummary.uncovered_scenarios,
      coverageAdequacy.uncovered_scenarios,
      portfolioSummary.uncovered_scenarios
    );

  const activeCoverages =
    resolveNumber(
      adequacySummary.active_coverages,
      coverageAdequacy.active_coverages,
      getCoverageCount(
        coverages,
        isActiveCoverage
      )
    );

  const expiringCoverages =
    resolveNumber(
      adequacySummary.expiring_coverages,
      coverageAdequacy.expiring_coverages,
      getCoverageCount(
        coverages,
        isExpiringCoverage
      )
    );

  return {
    available: true,

    status:
      engine.status?.hasError
        ? "error"
        : engine.status?.isLoading
          ? "loading"
          : "ready",

    overallPosition: {
      expectedAnnualLoss,
      capitalAtRiskP95,
      residualRisk,
      coverageRatio,
      scenariosWithProtectionGaps,
      activeScenarios,
      coveredScenarios,
      uncoveredScenarios,
      activeCoverages,
      expiringCoverages,
      riskAppetitePosition:
        engine.appetite?.analysis?.appetite?.overall_status ||
        portfolioAppetite.overall_status ||
        portfolioAppetite.overallStatus ||
        portfolioSummary.risk_appetite_position ||
        portfolioSummary.riskAppetitePosition ||
        null,
    },

    recommendations:
      engine.decisionAnalysis
        ?.recommendations ||
      engine.portfolio?.recommendations ||
      [],

    sourceData: {
      portfolio: {
        data:
          engine.portfolio?.data || null,

        summary:
          engine.portfolio?.summary || {},

        kpis:
          engine.portfolio?.kpis || {},

        curve:
          engine.portfolio?.curve || [],

        recommendations:
          engine.portfolio
            ?.recommendations || [],

        strategy:
          engine.portfolio?.strategy || null,

        state:
          engine.portfolio?.state || {},
      },

      scenario: {
        list:
          engine.scenario?.list || [],

        selectedId:
          engine.scenario?.selectedId ?? null,

        selected:
          engine.scenario?.selected || null,

        strategy:
          engine.scenario?.strategy || null,

        analysis:
          engine.scenario?.analysis || null,

        alternatives:
          engine.scenario?.alternatives || [],

        recommendations:
          engine.scenario
            ?.recommendations || [],

        state:
          engine.scenario?.state || {},
      },

      coverage: {
        portfolio:
          engine.coverage?.portfolio || [],

        scenarios:
          engine.coverage?.scenarios || [],

        adequacy:
          engine.coverage?.adequacy || null,

        summary:
          engine.coverage?.summary || {},

        active:
          engine.coverage?.active || [],

        expiring:
          engine.coverage?.expiring || [],

        state:
          engine.coverage?.state || {},
      },

      decisionAnalysis: {
        analysis:
          engine.decisionAnalysis
            ?.analysis || null,

        recommendations:
          engine.decisionAnalysis
            ?.recommendations || [],

        alternatives:
          engine.decisionAnalysis
            ?.alternatives || [],

        selectedAlternative:
          engine.decisionAnalysis
            ?.selectedAlternative || null,

        state:
          engine.decisionAnalysis
            ?.state || {},
      },

      status:
        engine.status || {},

      metadata:
        engine.metadata || {},
    },
  };
}


export default adaptRiskFinancingEngine;
